import { useState, useEffect, useCallback } from "react";
import { X } from "lucide-react";
import { formatBRL } from "@caixa/lib/format";
import { fetchRelatorioDia } from "@caixa/lib/api";
import type { RelatorioDiaCompleto } from "@caixa/relatorio/types";
import { RelatorioGerarModal } from "@caixa/relatorio/RelatorioGerarModal";

interface RelatorioCaixaModalProps {
  open: boolean;
  onClose: () => void;
  /** Quando incrementado, refaz o fetch do relatório (ex.: após movimento). */
  refreshTrigger?: number;
}

const CARDS = [
  { key: "faturamento" as const, label: "Faturamento" },
  { key: "credito" as const, label: "Crédito" },
  { key: "debito" as const, label: "Débito" },
  { key: "dinheiro" as const, label: "Dinheiro" },
  { key: "pix" as const, label: "Pix" },
  { key: "desperdicios" as const, label: "Desperdícios" },
] as const;

export function RelatorioCaixaModal({ open, onClose, refreshTrigger = 0 }: RelatorioCaixaModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<RelatorioDiaCompleto | null>(null);
  const [gerarOpen, setGerarOpen] = useState(false);

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchRelatorioDia();
      setData(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erro ao carregar relatório");
    } finally {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  useEffect(() => {
    if (open && refreshTrigger > 0) void load();
  }, [refreshTrigger, open, load]);

  useEffect(() => {
    if (!open) {
      setGerarOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || gerarOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, gerarOpen, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        className="caixa-overlay caixa-overlay--55"
        role="dialog"
        aria-modal="true"
        aria-labelledby="relatorio-caixa-title"
      >
        <div className="caixa-modal caixa-modal--xl caixa-modal--stack">
          <div className="caixa-modal-section--head">
            <h2 id="relatorio-caixa-title" className="caixa-modal-title">
              Relatório de Caixa {data?.date ? `– ${data.date}` : ""}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="caixa-modal-close"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" aria-hidden />
            </button>
          </div>
          <div className="caixa-modal-section--body">
            {loading && !data && <p className="text-[#a3a3a3]">Carregando...</p>}
            {error && <p className="caixa-error">{error}</p>}
            {data && !error && (
              <>
                <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
                  <div className="rounded-lg border border-[#3a3a3a] bg-[#242424] p-3">
                    <span className="block text-sm text-[#a3a3a3]">{CARDS[0].label}</span>
                    <span className="text-lg font-bold text-[#f5f5f5]">{formatBRL(data.faturamento_total)}</span>
                  </div>
                  {(["credito", "debito", "dinheiro", "pix"] as const).map((k) => (
                    <div key={k} className="rounded-lg border border-[#3a3a3a] bg-[#242424] p-3">
                      <span className="block text-sm text-[#a3a3a3]">
                        {CARDS.find((c) => c.key === k)?.label ?? k}
                      </span>
                      <span className="text-lg font-bold text-[#f5f5f5]">{formatBRL(data.pagamentos[k])}</span>
                    </div>
                  ))}
                  <div className="rounded-lg border border-[#3a3a3a] bg-[#242424] p-3">
                    <span className="block text-sm text-[#a3a3a3]">Desperdícios</span>
                    <span className="text-lg font-bold text-red-400">{formatBRL(data.desperdicios)}</span>
                  </div>
                </div>
                <div className="rounded-lg border-2 border-emerald-600 bg-emerald-900/40 p-4">
                  <span className="mb-1 block font-medium text-emerald-200">Valor em caixa (dinheiro físico)</span>
                  <span className="text-2xl font-bold text-white">{formatBRL(data.valor_em_caixa)}</span>
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setGerarOpen(true)}
                    disabled={loading || !!error || !data}
                    className="caixa-btn-primary"
                    style={{ flex: "0 0 auto", width: "auto" }}
                  >
                    Gerar relatório
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <RelatorioGerarModal
        open={gerarOpen}
        onClose={() => setGerarOpen(false)}
        onCloseAll={() => {
          setGerarOpen(false);
          onClose();
        }}
        report={data}
        loadingReport={loading && !data}
        onReportUpdated={(r) => setData(r)}
      />
    </>
  );
}
